<template>
  <div
    class="adp"
    :class="{ 'is-open': open, 'is-invalid': invalid, 'is-disabled': disabled }"
  >
    <button
      ref="trigger"
      type="button"
      class="adp__trigger"
      :id="id"
      :disabled="disabled"
      aria-haspopup="dialog"
      :aria-expanded="open ? 'true' : 'false'"
      :aria-label="ariaLabel || placeholder"
      @click="toggle"
      @keydown.down.prevent="openPanel"
    >
      <span class="adp__value" :class="{ 'is-placeholder': !hasValue }">
        {{ hasValue ? displayValue : placeholder }}
      </span>
      <svg
        class="adp__icon"
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="1.8"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden="true"
        focusable="false"
      >
        <path d="M8 2v4M16 2v4" />
        <path d="M5 4h14a2 2 0 0 1 2 2v13a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z" />
        <path d="M3 10h18" />
      </svg>
    </button>

    <teleport to="body">
      <div
        v-if="open"
        class="adp-layer"
        :class="{ 'adp-layer--sheet': sheet }"
        @mousedown.self="close()"
       
      >
        <div
          ref="panel"
          class="adp-panel"
          :class="{ 'adp-panel--sheet': sheet }"
          :style="panelStyle"
          role="dialog"
          aria-modal="true"
          :aria-label="ariaLabel || placeholder || 'Choose a date'"
          @keydown="onPanelKeydown"
        >
          <div class="adp-head">
            <button
              type="button"
              class="adp-nav"
              aria-label="Previous month"
              :disabled="prevDisabled"
              @click="shiftMonth(-1)"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M15 6l-6 6 6 6" /></svg>
            </button>
            <div class="adp-selects">
              <select
                class="adp-select"
                aria-label="Month"
                :value="viewMonth"
                @change="onMonthSelect($event.target.value)"
              >
                <option
                  v-for="(m, i) in monthNames"
                  :key="m"
                  :value="i"
                  :disabled="isMonthDisabled(viewYear, i)"
                >
                  {{ m }}
                </option>
              </select>
              <select
                class="adp-select"
                aria-label="Year"
                :value="viewYear"
                @change="onYearSelect($event.target.value)"
              >
                <option v-for="y in yearOptions" :key="y" :value="y">{{ y }}</option>
              </select>
            </div>
            <button
              type="button"
              class="adp-nav"
              aria-label="Next month"
              :disabled="nextDisabled"
              @click="shiftMonth(1)"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 6l6 6-6 6" /></svg>
            </button>
          </div>

          <div class="adp-grid adp-weekdays" aria-hidden="true">
            <span v-for="d in weekdays" :key="d">{{ d }}</span>
          </div>

          <div class="adp-grid adp-days" role="grid">
            <button
              v-for="cell in cells"
              :key="cell.iso"
              type="button"
              class="adp-day"
              :class="{
                'is-outside': cell.outside,
                'is-today': cell.iso === todayIso,
                'is-selected': cell.iso === selectedIso,
              }"
              :data-iso="cell.iso"
              :disabled="cell.disabled"
              :tabindex="cell.iso === focusIso ? 0 : -1"
              :aria-label="cell.label"
              :aria-selected="cell.iso === selectedIso ? 'true' : 'false'"
              @click="pick(cell.iso)"
            >
              {{ cell.day }}
            </button>
          </div>

          <div class="adp-foot">
            <button
              type="button"
              class="adp-link"
              :disabled="!hasValue"
              @click="clear"
            >
              Clear
            </button>
            <button
              type="button"
              class="adp-link adp-link--accent"
              :disabled="isDisabledIso(todayIso)"
              @click="pick(todayIso)"
            >
              Today
            </button>
          </div>
        </div>
      </div>
    </teleport>
  </div>
</template>

<script>
/**
 * AppDatePicker - the single date picker used across the application.
 *
 * v-model is an ISO "YYYY-MM-DD" string (same value a native
 * <input type="date"> produced), so validation and API code are unchanged.
 *
 *   <AppDatePicker v-model="form.personal1.dob" placeholder="Select date"
 *                  past-only />
 *
 * Props
 *   modelValue   ISO date string or ""
 *   placeholder  text shown when empty
 *   min / max    ISO bounds (days outside are disabled)
 *   past-only    shortcut: disables today and every future date
 *   invalid      adds the `is-invalid` hook class
 *   disabled     read-only display
 */
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const MONTHS_SHORT = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];
const ISO_RE = /^(\d{4})-(\d{2})-(\d{2})/;
const SHEET_BREAKPOINT = 575;
const PANEL_WIDTH = 304;

const pad = (n) => String(n).padStart(2, "0");
const toIso = (y, m, d) => `${y}-${pad(m + 1)}-${pad(d)}`;
const daysIn = (y, m) => new Date(y, m + 1, 0).getDate();

function parseIso(value) {
  const m = ISO_RE.exec(String(value || ""));
  if (!m) return null;
  const y = +m[1];
  const mo = +m[2] - 1;
  const d = +m[3];
  if (mo < 0 || mo > 11 || d < 1 || d > daysIn(y, mo)) return null;
  return { y, m: mo, d };
}

function addDaysIso(iso, delta) {
  const p = parseIso(iso);
  const dt = new Date(p.y, p.m, p.d + delta);
  return toIso(dt.getFullYear(), dt.getMonth(), dt.getDate());
}

export default {
  name: "AppDatePicker",
  props: {
    modelValue: { type: String, default: "" },
    placeholder: { type: String, default: "Select date" },
    ariaLabel: { type: String, default: "" },
    id: { type: String, default: undefined },
    min: { type: String, default: "" },
    max: { type: String, default: "" },
    pastOnly: { type: Boolean, default: false },
    invalid: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
  },
  emits: ["update:modelValue", "change"],
  data() {
    return {
      open: false,
      sheet: false,
      panelStyle: {},
      viewYear: new Date().getFullYear(),
      viewMonth: new Date().getMonth(),
      focusIso: "",
    };
  },
  computed: {
    monthNames: () => MONTHS,
    weekdays: () => ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"],
    todayIso() {
      const t = new Date();
      return toIso(t.getFullYear(), t.getMonth(), t.getDate());
    },
    parsed() {
      return parseIso(this.modelValue);
    },
    hasValue() {
      return !!this.parsed;
    },
    selectedIso() {
      return this.parsed ? toIso(this.parsed.y, this.parsed.m, this.parsed.d) : "";
    },
    displayValue() {
      const p = this.parsed;
      return p ? `${pad(p.d)} ${MONTHS_SHORT[p.m]} ${p.y}` : "";
    },
    minIso() {
      const p = parseIso(this.min);
      return p ? toIso(p.y, p.m, p.d) : "";
    },
    maxIso() {
      const own = parseIso(this.max);
      const ownIso = own ? toIso(own.y, own.m, own.d) : "";
      if (!this.pastOnly) return ownIso;
      const yesterday = addDaysIso(this.todayIso, -1);
      return ownIso && ownIso < yesterday ? ownIso : yesterday;
    },
    yearOptions() {
      const now = new Date().getFullYear();
      let from = this.minIso ? +this.minIso.slice(0, 4) : now - 120;
      let to = this.maxIso ? +this.maxIso.slice(0, 4) : now + 30;
      if (this.parsed) {
        from = Math.min(from, this.parsed.y);
        to = Math.max(to, this.parsed.y);
      }
      const years = [];
      for (let y = to; y >= from; y--) years.push(y);
      return years;
    },
    cells() {
      const y = this.viewYear;
      const m = this.viewMonth;
      const lead = new Date(y, m, 1).getDay();
      const out = [];
      for (let i = 0; i < 42; i++) {
        const dt = new Date(y, m, 1 - lead + i);
        const iso = toIso(dt.getFullYear(), dt.getMonth(), dt.getDate());
        out.push({
          iso,
          day: dt.getDate(),
          outside: dt.getMonth() !== m,
          disabled: this.isDisabledIso(iso),
          label: `${dt.getDate()} ${MONTHS[dt.getMonth()]} ${dt.getFullYear()}`,
        });
      }
      return out;
    },
    prevDisabled() {
      if (!this.minIso) return false;
      return toIso(this.viewYear, this.viewMonth, 1) <= this.minIso;
    },
    nextDisabled() {
      if (!this.maxIso) return false;
      const last = toIso(this.viewYear, this.viewMonth, daysIn(this.viewYear, this.viewMonth));
      return last >= this.maxIso;
    },
  },
  watch: {
    open(isOpen) {
      if (isOpen) {
        window.addEventListener("resize", this.place);
        window.addEventListener("scroll", this.place, true);
        document.addEventListener("keydown", this.onDocKeydown);
      } else {
        this.removeListeners();
      }
    },
  },
  beforeUnmount() {
    this.removeListeners();
  },
  methods: {
    removeListeners() {
      window.removeEventListener("resize", this.place);
      window.removeEventListener("scroll", this.place, true);
      document.removeEventListener("keydown", this.onDocKeydown);
    },
    isDisabledIso(iso) {
      if (!iso) return true;
      if (this.minIso && iso < this.minIso) return true;
      if (this.maxIso && iso > this.maxIso) return true;
      return false;
    },
    isMonthDisabled(y, m) {
      const first = toIso(y, m, 1);
      const last = toIso(y, m, daysIn(y, m));
      return (
        (!!this.minIso && last < this.minIso) ||
        (!!this.maxIso && first > this.maxIso)
      );
    },
    clampToRange(iso) {
      if (this.minIso && iso < this.minIso) return this.minIso;
      if (this.maxIso && iso > this.maxIso) return this.maxIso;
      return iso;
    },
    toggle() {
      this.open ? this.close() : this.openPanel();
    },
    openPanel() {
      if (this.disabled || this.open) return;
      const start = this.clampToRange(this.selectedIso || this.todayIso);
      const p = parseIso(start);
      this.viewYear = p.y;
      this.viewMonth = p.m;
      this.focusIso = start;
      this.sheet = window.innerWidth <= SHEET_BREAKPOINT;
      this.open = true;
      this.$nextTick(() => {
        this.place();
        this.focusDay();
      });
    },
    close(refocus = false) {
      this.open = false;
      if (refocus) this.$nextTick(() => this.$refs.trigger && this.$refs.trigger.focus());
    },
    place() {
      const trigger = this.$refs.trigger;
      if (!this.open || !trigger) return;
      this.sheet = window.innerWidth <= SHEET_BREAKPOINT;
      if (this.sheet) {
        this.panelStyle = {};
        return;
      }
      const r = trigger.getBoundingClientRect();
      const panel = this.$refs.panel;
      const h = panel ? panel.offsetHeight : 360;
      const left = Math.min(Math.max(8, r.left), window.innerWidth - PANEL_WIDTH - 8);
      let top = r.bottom + 6;
      if (top + h > window.innerHeight - 8) {
        const above = r.top - h - 6;
        top = above >= 8 ? above : Math.max(8, window.innerHeight - h - 8);
      }
      this.panelStyle = {
        top: `${top}px`,
        left: `${left}px`,
        width: `${PANEL_WIDTH}px`,
      };
    },
    focusDay() {
      this.$nextTick(() => {
        const panel = this.$refs.panel;
        if (!panel) return;
        const el = panel.querySelector(`[data-iso="${this.focusIso}"]:not(:disabled)`);
        if (el) el.focus({ preventScroll: true });
      });
    },
    pick(iso) {
      if (this.isDisabledIso(iso)) return;
      this.$emit("update:modelValue", iso);
      this.$emit("change", iso);
      this.close(true);
    },
    clear() {
      this.$emit("update:modelValue", "");
      this.$emit("change", "");
      this.close(true);
    },
    setView(y, m) {
      let ny = y;
      let nm = m;
      if (nm < 0) { nm = 11; ny -= 1; }
      if (nm > 11) { nm = 0; ny += 1; }
      const first = toIso(ny, nm, 1);
      const last = toIso(ny, nm, daysIn(ny, nm));
      if (this.minIso && last < this.minIso) {
        const p = parseIso(this.minIso);
        ny = p.y; nm = p.m;
      } else if (this.maxIso && first > this.maxIso) {
        const p = parseIso(this.maxIso);
        ny = p.y; nm = p.m;
      }
      this.viewYear = ny;
      this.viewMonth = nm;
    },
    shiftMonth(delta) {
      this.setView(this.viewYear, this.viewMonth + delta);
    },
    onMonthSelect(value) {
      this.setView(this.viewYear, +value);
    },
    onYearSelect(value) {
      this.setView(+value, this.viewMonth);
    },
    moveFocus(iso) {
      const target = this.clampToRange(iso);
      const p = parseIso(target);
      this.focusIso = target;
      this.viewYear = p.y;
      this.viewMonth = p.m;
      this.focusDay();
    },
    onPanelKeydown(e) {
      const onDay = e.target && e.target.classList && e.target.classList.contains("adp-day");
      if (!onDay) return;
      const cur = this.focusIso;
      const map = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 };
      if (e.key in map) {
        e.preventDefault();
        this.moveFocus(addDaysIso(cur, map[e.key]));
      } else if (e.key === "PageUp" || e.key === "PageDown") {
        e.preventDefault();
        const p = parseIso(cur);
        const dir = e.key === "PageUp" ? -1 : 1;
        const first = new Date(p.y, p.m + dir, 1);
        const y = first.getFullYear();
        const m = first.getMonth();
        this.moveFocus(toIso(y, m, Math.min(p.d, daysIn(y, m))));
      } else if (e.key === "Home") {
        e.preventDefault();
        this.moveFocus(toIso(this.viewYear, this.viewMonth, 1));
      } else if (e.key === "End") {
        e.preventDefault();
        this.moveFocus(toIso(this.viewYear, this.viewMonth, daysIn(this.viewYear, this.viewMonth)));
      }
    },
    onDocKeydown(e) {
      if (e.key === "Escape") {
        e.stopPropagation();
        this.close(true);
      }
    },
  },
};
</script>

<style>
/* ==========================================================================
   AppDatePicker. The panel is teleported to <body>, so these rules are
   namespaced `adp-*` and use literal colours (no .ecu-apply variables).
   ========================================================================== */
.adp {
  position: relative;
  width: 100%;
}
.adp__trigger {
  width: 100%;
  min-height: var(--ea-field-h, 42px);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 10px 14px;
  font-family: inherit;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.4;
  text-align: left;
  color: var(--ea-ink, #1d2a4d);
  background: #fff;
  border: 1px solid var(--ea-field-border, #c1c4cb);
  border-radius: 10px;
  cursor: pointer;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}
.adp__trigger:hover:not(:disabled) {
  border-color: var(--ea-field-border-hover, #a9acb5);
}
.adp__trigger:focus-visible,
.adp.is-open .adp__trigger {
  outline: 0;
  border-color: var(--ea-orange, #f97316);
  box-shadow: 0 0 0 3px rgba(249, 115, 22, 0.16);
}
.adp__value {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.adp__value.is-placeholder {
  color: var(--ea-placeholder, #9aa1b5);
}
.adp__icon {
  flex: 0 0 auto;
  color: #7b8194;
}
.adp.is-open .adp__icon {
  color: var(--ea-orange, #f97316);
}
.adp.is-disabled .adp__trigger {
  background: #f4f6fb;
  cursor: not-allowed;
}

.adp-layer {
  position: fixed;
  inset: 0;
  z-index: 1080;
}
.adp-layer--sheet {
  display: flex;
  align-items: flex-end;
  background: rgba(11, 31, 75, 0.45);
}
.adp-panel {
  position: fixed;
  box-sizing: border-box;
  padding: 12px;
  font-family: "Plus Jakarta Sans", "Inter", "Segoe UI", system-ui, -apple-system, sans-serif;
  font-size: 14px;
  color: #1d2a4d;
  background: #fff;
  border: 1px solid #e3e6ef;
  border-radius: 14px;
  box-shadow: 0 18px 40px rgba(20, 40, 90, 0.18);
  animation: adp-pop 0.14s ease both;
}
.adp-panel *,
.adp-panel *::before,
.adp-panel *::after {
  box-sizing: border-box;
}
.adp-panel--sheet {
  position: relative;
  width: 100%;
  max-width: 420px;
  margin: 0 auto;
  padding: 16px 16px calc(16px + env(safe-area-inset-bottom, 0px));
  border-radius: 18px 18px 0 0;
  border-bottom: 0;
  animation: adp-sheet 0.2s ease both;
}
@keyframes adp-pop {
  from { opacity: 0; transform: translateY(-4px); }
  to { opacity: 1; transform: none; }
}
@keyframes adp-sheet {
  from { transform: translateY(40px); opacity: 0; }
  to { transform: none; opacity: 1; }
}

.adp-head {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
}
.adp-selects {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  gap: 6px;
}
.adp-select {
  flex: 1 1 0;
  min-width: 0;
  height: 34px;
  padding: 0 8px;
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  color: #0b1f4b;
  background: #fff;
  border: 1px solid #d7dae4;
  border-radius: 8px;
  cursor: pointer;
}
.adp-select:focus-visible {
  outline: 0;
  border-color: #f97316;
  box-shadow: 0 0 0 3px rgba(249, 115, 22, 0.16);
}
.adp-nav {
  flex: 0 0 34px;
  width: 34px;
  height: 34px;
  display: grid;
  place-items: center;
  color: #0b1f4b;
  background: #f3f4f9;
  border: 0;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}
.adp-nav:hover:not(:disabled) {
  color: #fff;
  background: #f97316;
}
.adp-nav:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

.adp-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 2px;
}
.adp-weekdays span {
  padding: 6px 0;
  font-size: 11px;
  font-weight: 700;
  text-align: center;
  color: #7b8194;
  text-transform: uppercase;
}
.adp-day {
  height: 36px;
  padding: 0;
  font-family: inherit;
  font-size: 13px;
  font-weight: 500;
  color: #1d2a4d;
  background: transparent;
  border: 1px solid transparent;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.12s ease, color 0.12s ease;
}
.adp-day:hover:not(:disabled):not(.is-selected) {
  background: #fff1e6;
}
.adp-day:focus-visible {
  outline: 0;
  box-shadow: 0 0 0 2px #f97316;
}
.adp-day.is-outside {
  color: #aab0c2;
}
.adp-day.is-today {
  font-weight: 700;
  border-color: #f8cfae;
}
.adp-day.is-selected {
  font-weight: 700;
  color: #fff;
  background: linear-gradient(135deg, #ff7a1a, #f2610f);
  border-color: transparent;
}
.adp-day:disabled {
  color: #c4c8d6;
  background: transparent;
  text-decoration: line-through;
  text-decoration-color: rgba(196, 200, 214, 0.7);
  cursor: not-allowed;
}
.adp-day.is-selected:disabled {
  color: #fff;
  background: #f5b78a;
  text-decoration: none;
}

.adp-foot {
  display: flex;
  justify-content: space-between;
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid #eceef5;
}
.adp-link {
  padding: 6px 10px;
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  color: #5f6579;
  background: none;
  border: 0;
  border-radius: 8px;
  cursor: pointer;
}
.adp-link:hover:not(:disabled) {
  background: #f3f4f9;
}
.adp-link--accent {
  color: #e84d00;
}
.adp-link:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

@media (max-width: 767.98px) {
  /* same 6px right gap the other form fields keep inside the wizard card */
  .ecu-apply .adp__trigger {
    width: calc(100% - 6px);
    margin-right: 6px;
  }
}
@media (prefers-reduced-motion: reduce) {
  .adp-panel,
  .adp-panel--sheet {
    animation: none;
  }
}
</style>